import Image from 'next/image'
import Link from 'next/link'
import React, { useState, useRef, useEffect } from 'react'
import { FaArrowRight, FaChevronRight } from 'react-icons/fa'
import { s3Static } from 'utils/constants'

export const TrainerByType = () => {
    const [showArrow, setShowArrow] = useState(true);
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;

        const handleScroll = () => {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
            const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 10;
            setShowArrow(!isAtEnd);
        };

        scrollContainer.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToNext = () => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;

        const cardWidth = scrollContainer.querySelector('section[data-card]')?.offsetWidth || 0;
        const gap = 24; // gap-6 = 1.5rem = 24px
        scrollContainer.scrollBy({
            left: cardWidth + gap,
            behavior: 'smooth'
        });
    };

    // Data list for fitness goals
    const trainerType = [
    {
        title: "Female Trainer",
        desc: "Our female personal trainers understand women's unique fitness needs and create supportive environments for building confidence, strength, and health through personalized programs.",
        href: "/sg/personal-trainer/personal-trainer-singapore-female",
        buttonText: "Female Personal Trainer"
    },
    {
        title: "Freelance Trainer",
        desc: "Choose from our network of independent freelance personal trainers in Singapore who offer flexible scheduling and competitive rates.",
        href: "/sg/personal-trainer/freelance-personal-trainer-singapore",
        buttonText: "Freelance Personal Trainer"
    },
    {
        title: "Trainer for Elderly",
        desc: "Our personal trainer for seniors focus on safe exercises that improve mobility, strength, balance, and quality of life for older adults.",
        href: "/sg/personal-trainer/personal-trainer-for-elderly",
        buttonText: "Personal Trainer For Elderly"
    },
    {
        title: "Private Home Trainer",
        desc: "Experience convenient home personal training with StringsSG employed fitness trainers for weight loss, muscle building, or general health improvement in the comfort of your own space.",
        href: "/sg/personal-trainer/private-home-trainer",
        buttonText: "Home Trainer"
    },
    {
        title: "Group Fitness Trainer",
        desc: "Experience motivating, community-driven workout experiences with high-quality instruction in various group fitness classes in Singapore led by certified trainers.",
        href: "/blogs/fitness-classes-in-singapore",
        buttonText: "Group Trainers"
    },
    {
        title: "Pre & Postnatal Fitness Trainer",
        desc: "Trust our certified pre and postnatal personal trainers in Singapore experts for safe fitness journeys during pregnancy and postpartum recovery.",
        href: "/sg/personal-trainer/prenatal-postpartum-training",
        buttonText: "Pre & Postnatal Trainer"
    }
  ];

  return (
    <section className="max-w-6xl mx-auto my-10 relative">
        <div className='max-w-3xl mx-auto pt-2 text-sm leading-7 space-y-5'>
            <div className='relative w-full h-60 md:h-80'>
                <Image
                    src={s3Static + "personal-trainer/female-personal-trainer-training-girl-at-gym-hd.jpeg"}
                    fill
                    style={{objectFit: 'cover'}}
                    placeholder='blur'
                    blurDataURL={s3Static + "placeholder.png"}
                    alt='Personal Trainer Types Singapore - Home, Female, Senior, Freelance Trainers | StringsSG'
                    sizes="(max-width: 768px) 50vw, 100vw"
                />
            </div>
        </div>
        <div className="relative mt-10">
            {/* Scroll Indicator - Only visible on mobile when not at end */}
            {showArrow && (
                <div 
                    className="absolute right-2 top-1/2 -translate-y-1/2 md:hidden z-10 cursor-pointer"
                    onClick={scrollToNext}
                >
                    <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg animate-pulse">
                        <FaChevronRight className="text-2xl text-orange-500" />
                    </div>
                </div>
            )}

            <div
                ref={scrollContainerRef}
                className="flex md:grid md:grid-cols-2 gap-2 overflow-x-auto md:overflow-x-visible snap-x snap-mandatory md:snap-none scroll-smooth pb-4 scrollbar-hide"
                role="region"
                aria-label="Trainer types by goal"
            >
                {trainerType.map(({ title, href, desc, buttonText }) => (
                <section
                    key={title}
                    data-card
                    className="border rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-shadow bg-white shrink-0 w-[80vw] sm:w-[70vw] md:w-auto snap-center"
                    aria-labelledby={`${title.toLowerCase().replace(/\s+/g, "-")}-heading`}
                >
                <div className="p-5 text-gray-700 text-base leading-relaxed flex flex-col justify-between h-full">
                    <div>
                        <div className="flex flex-col items-center text-center space-y-5 mb-4">
                            <h3 id={`${title.toLowerCase().replace(/\s+/g, "-")}-heading`} className="text-lg md:text-xl font-semibold">
                                {title}
                            </h3>
                        </div>
                        <div className='text-sm md:text-base leading-6' dangerouslySetInnerHTML={{ __html: desc }} />
                    </div>
                    <div className="text-center pt-4 pb-2">
                        <Link
                            href={href}
                            prefetch={false}
                            aria-label={`Explore ${title}`}
                            className="inline-flex items-center px-6 py-3 rounded-2xl bg-orange-500 text-white text-sm md:text-base font-semibold hover:bg-orange-600 transition"
                        >
                            {buttonText} <FaArrowRight className="ml-2" />
                        </Link>
                    </div>
                </div>
            </section>
                ))}
            </div>
        </div>
    </section>
  )
}
